import { getUserDetails } from "./activityCatalogScope";

export const SUPPORTED_DATE_FORMATS = ["DD-MM-YYYY", "MM-DD-YYYY", "YYYY-MM-DD"];
export const DEFAULT_DATE_FORMAT = "DD-MM-YYYY";

/** Reads the current school's configured display date format from the cached session (userDetail). */
export const resolveSchoolDateFormat = (userDetails = getUserDetails()) => {
  const value =
    userDetails?.dateFormat ||
    userDetails?.schooleId?.dateFormat ||
    userDetails?.schoolId?.dateFormat;
  return SUPPORTED_DATE_FORMATS.includes(value) ? value : DEFAULT_DATE_FORMAT;
};

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Formats a stored date value (ISO string "YYYY-MM-DD"/full ISO, or a Date) for display,
 * using the given school date format. Storage/query values are never touched by this —
 * it only affects what's rendered on screen.
 */
export const formatDate = (value, schoolDateFormat = resolveSchoolDateFormat()) => {
  if (!value) return "";

  let year;
  let month;
  let day;

  // Parse a plain "YYYY-MM-DD" (date-only) string directly to avoid the
  // UTC-midnight/local-timezone shift that new Date("YYYY-MM-DD") can introduce.
  const isoDateOnlyMatch = typeof value === "string" && value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDateOnlyMatch) {
    [, year, month, day] = isoDateOnlyMatch;
    year = Number(year);
  } else {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    year = d.getFullYear();
    month = pad2(d.getMonth() + 1);
    day = pad2(d.getDate());
  }

  const format = SUPPORTED_DATE_FORMATS.includes(schoolDateFormat)
    ? schoolDateFormat
    : DEFAULT_DATE_FORMAT;

  switch (format) {
    case "MM-DD-YYYY":
      return `${month}-${day}-${year}`;
    case "YYYY-MM-DD":
      return `${year}-${month}-${day}`;
    case "DD-MM-YYYY":
    default:
      return `${day}-${month}-${year}`;
  }
};

/**
 * Short hint for native `<input type="date">` fields (which can't have their own
 * picker UI restyled) telling the user which format the date will be displayed in
 * elsewhere in the app, e.g. for a TextField's `helperText` prop.
 */
export const dateFormatHint = (schoolDateFormat = resolveSchoolDateFormat()) => {
  const format = SUPPORTED_DATE_FORMATS.includes(schoolDateFormat)
    ? schoolDateFormat
    : DEFAULT_DATE_FORMAT;
  return `Format: ${format}`;
};

/** Loosely matches the date-format feature regardless of naming style (dateFormat, date_format, "Date Format", ...). */
const isDateFormatFeatureName = (name) =>
  String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "") === "dateformat";

/** Normalizes a date-format value entered with any common separator (/, ., -) to our canonical dash form. */
const normalizeDateFormatValue = (value) => {
  const upper = String(value || "").trim().toUpperCase().replace(/[./]/g, "-");
  return SUPPORTED_DATE_FORMATS.includes(upper) ? upper : null;
};

/**
 * Resolves the configured date format from an Origin Master `features` list (a "dateFormat"
 * / "date_format" entry, value in its `description`), falling back to the app default when
 * missing, disabled, or an unrecognized value. Never hardcodes a domain-specific format.
 */
export const resolveDateFormatFromFeatures = (features) => {
  const list = Array.isArray(features) ? features : [];
  const entry = list.find((f) => isDateFormatFeatureName(f?.name));
  if (!entry || entry.enabled === false) return DEFAULT_DATE_FORMAT;
  return normalizeDateFormatValue(entry.description) || DEFAULT_DATE_FORMAT;
};
